import { useCallback, useEffect, useRef, useState } from 'react';
import { clampPlayhead, replayIntervalMs } from '../engine/replay';

export function useBarReplay(barCount, { enabled = true } = {}) {
  const [playhead, setPlayheadState] = useState(() => Math.max(0, (barCount || 1) - 1));
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const playheadRef = useRef(playhead);
  playheadRef.current = playhead;

  useEffect(() => {
    if (!barCount) {
      setPlayheadState(0);
      return;
    }
    setPlayheadState((prev) => clampPlayhead(prev > barCount - 1 ? barCount - 1 : prev, barCount));
  }, [barCount]);

  const setPlayhead = useCallback((index) => {
    setPlayheadState(clampPlayhead(index, barCount));
  }, [barCount]);

  const step = useCallback((delta) => {
    setPlayheadState((prev) => clampPlayhead(prev + delta, barCount));
  }, [barCount]);

  const toEnd = useCallback(() => {
    setPlaying(false);
    setPlayheadState(Math.max(0, (barCount || 1) - 1));
  }, [barCount]);

  const toStart = useCallback(() => {
    setPlayheadState(0);
  }, []);

  useEffect(() => {
    if (!playing || !enabled || !barCount) return undefined;
    const id = setInterval(() => {
      const next = playheadRef.current + 1;
      if (next >= barCount) {
        setPlaying(false);
        setPlayheadState(barCount - 1);
        return;
      }
      setPlayheadState(next);
    }, replayIntervalMs(speed));
    return () => clearInterval(id);
  }, [playing, speed, barCount, enabled]);

  return {
    playhead,
    setPlayhead,
    playing,
    setPlaying,
    speed,
    setSpeed,
    step,
    toEnd,
    toStart,
  };
}
